<script setup lang="ts">
import SearchBar from './SearchBar.vue'
import { sortLinks } from './util'
import NavigationLink from './NavigationLink.vue'

const { navigation } = useContent()
const dm = useDM()

const open = ref(false)

const withoutIndex = computed(() => {
  const nodes = navigation.value.filter((item) => item._path !== '/')
  return sortLinks(nodes, dm.value)
})

const dmCookie = useCookie('dungeonMaster', { maxAge: 60 * 60 * 24 * 365 })
const dmPassword = (e: MouseEvent) => {
  e.preventDefault()
  const password = prompt('DM says what?')
  if (password) dmCookie.value = password
}
</script>

<template>
  <nav class="topmenu" :class="{ open }">
    <div class="menu-content">
      <div class="row">
        <Icon name="estia:triquetra" class="logo" @click="open = !open" @dblclick="dmPassword" />
        <NuxtLink to="/" class="index-link">
          <h3>The Estian Islands</h3>
        </NuxtLink>
        <div class="spacer"></div>
        <ColorSchemePicker />
        <SearchBar />
      </div>
      <div class="row links">
        <ClientOnly>
          <ul class="node-list open root">
            <NavigationLink v-for="node in withoutIndex" :key="node._id" :node="node" />
          </ul>
        </ClientOnly>
      </div>
    </div>
  </nav>
</template>

<style scoped lang="postcss">
.topmenu {
  grid-area: menu;
  display: flex;
  justify-content: center;
  padding: 0.5em 0;
  max-width: 100%;
  background: var(--background);
  box-shadow: var(--card-shadow);
  z-index: 100; /* ensure box-shadow is above pano */

  .menu-content {
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    width: calc(var(--content-width) + 2rem);
    gap: 1em;

    .logo {
      height: 42px;
      width: 42px;
      color: var(--text-highlight);
      transition: transform 0.66s ease-in;
    }

    .row {
      display: flex;
      align-items: center;
      gap: 1em;
      padding: 0 1em;
    }

    .spacer {
      flex: 1;
    }

    .index-link {
      h3 {
        margin: 0;
        line-height: 1.2;
      }
    }
  }

  h3 {
    font-size: 1.25em;
    font-weight: 500;
    display: inline-block;
    color: var(--primary);
    margin-bottom: 0.25em;
    margin-top: 1em;
  }

  .menu-content > .node-list {
    padding-left: 0;
  }

  @media (max-width: 950px) {
    h3 {
      font-size: 1.1em;
    }

    .menu-content {
      .logo {
        height: 32px !important;
        width: 32px !important;
        flex: 0 0 32px;
      }
      .row {
        gap: 0.5em;
        padding: 0 0.5em;
      }
      .links {
        display: none;
      }
    }
  }
}
</style>

<style lang="postcss">
.topmenu {
  .router-link-exact-active {
    font-weight: 500;
    --primary: var(--secondary);
  }

  .node-list {
    list-style: none;
  }

  @media screen and (min-width: 951px) {
    .opener {
      display: none;
    }

    .node-list.root {
      display: flex;
      flex: 1;
      flex-direction: row;
      gap: 0.25rem;
      padding: 0;
      justify-content: space-between;
      margin: 0;

      > .node {
        > .node-link,
        > .node-label {
          .chevron {
            display: none;
          }
        }
      }

      .node-list {
        display: none;
      }

      .node:hover {
        background-color: var(--bg-primary);

        > .node-link {
          .icon {
            color: var(--text-highlight);
          }
        }

        > .node-label {
          color: var(--text-highlight);
        }

        > .node-list {
          position: absolute;
          right: 0;
          top: 100%;
          width: max-content;
          display: flex;
          flex-direction: column;
          list-style: none;
          padding: 0;
          background: var(--background);
          box-shadow: var(--card-shadow);
          border-bottom: 2px solid var(--primary);
          z-index: 600;

          .node {
            padding: 0.25em 0.5em;
          }

          .node-list {
            left: 95%;
            top: 0;

            .node-list {
              right: 95%;
              left: unset;
            }
          }
        }
      }
    }
  }

  @media screen and (width < 950px) {
    display: contents !important;
    padding: 0;

    @media (width < 950px) {
      .node-list {
        display: none;
        width: 100%;
        flex-direction: column;

        .node.open {
          > .node-list {
            display: flex;
          }

          > :is(.node-label, .node-link) > .chevron {
            transform: rotate(90deg);
          }
        }
      }
    }

    .menu-content {
      display: block !important;
      position: fixed;
      top: 0;
      z-index: 400;
      padding: 0.5em 0;
      background: var(--background);
      box-shadow: var(--card-shadow);
    }

    .links {
      display: flex !important;
      align-items: stretch !important;
      justify-content: flex-start;
      position: fixed;
      flex: initial;
      background: var(--background);
      border-right: 2px solid var(--primary);
      top: 58.4px;
      left: calc(var(--sidebar-width) * -1);
      height: calc(100dvh - 58.4px);
      width: calc(var(--sidebar-width));
      max-width: 80vw;
      z-index: 350;
      transition: left 0.66s ease-in;
      box-shadow: var(--card-shadow);

      .node-list {
        padding-left: 0.75em;

        .node {
          flex-direction: column;
          align-items: stretch;
          justify-content: space-between;

          .chevron {
            transition: transform 0.33s;
          }
        }
      }

      > .node-list {
        display: flex;
        padding: 0.5em;
        margin: 0;
        max-height: 100%;
        overflow-y: scroll;
      }
    }

    &.open {
      .logo {
        transform: rotate(90deg);
      }
      .links {
        left: 0;
      }
    }
  }
}
</style>
